import React from 'react';
import { connect } from 'react-redux';
import { useIntl } from 'react-intl';
import { Form, Input } from 'semantic-ui-react';
import isNumeric from 'validator/es/lib/isNumeric';
import {
  clientError,
  firstError,
  firstResponseError,
  pruneServerErrors,
  invalidUrlCharacters, validationClasses,
} from '@/utils/errors';
import { states } from '@/constants/lifecycle';
import fields, { globalFieldLengths } from '@/constants/fields';
import urlDenyCharacters from '@/constants/validation';
import FormValidationError from '@/components/errors/FormValidationError';
import Dialog from '@/components/dialogs/Dialog';
import { hideSaveCollection, createCollection, updateActiveCollectionProperties } from '../actions';

const SaveCollectionDialog = ({
  code,
  hide,
  name,
  postData,
  saveErrors,
  saveStatus,
  theme,
  updateProperties,
}) => {
  const intl = useIntl();
  const setErrors = (newErrors) => (updateProperties({ saveErrors: newErrors }));

  const setStatus = (status) => (updateProperties({ saveStatus: status }));

  const handleClose = () => {
    hide();
  };

  const codeChange = (e) => {
    updateProperties({ code: e.target.value });
  };

  const handleDismiss = () => {
    setErrors(pruneServerErrors(saveErrors));
  };

  const validateForm = () => {
    const validationErrors = [];

    if (code && (isNumeric(code) || invalidUrlCharacters(code))) {
      validationErrors.push(clientError(
        { entity: 'collection' },
        intl.formatMessage({ id: 'save.collection.validation.code.title' }),
        intl.formatMessage({ id: 'validation.invalid.code' }, { characters: urlDenyCharacters }),
        [fields.CODE],
      ));
    }

    return validationErrors;
  };

  const checkForErrors = () => {
    const validationErrors = validateForm();
    setErrors(validationErrors);

    return validationErrors;
  };

  const submitForm = () => {
    setStatus(states.PENDING);
    const validationErrors = checkForErrors();

    if (validationErrors.length > 0) {
      setStatus(states.NONE);
      return;
    }

    postData();
  };

  const codeError = saveErrors ? saveErrors.find((e) => (e.fields.includes(fields.CODE))) : null;

  return (
    <Dialog
      className="aridhia-save-collection-dialog"
      error={firstResponseError(saveErrors)}
      icon="table"
      inProgress={saveStatus === states.PENDING}
      message="collection.create.text"
      messageicon="save"
      minimumDataEntryMet={name}
      onClose={handleClose}
      onDismiss={handleDismiss}
      onSave={submitForm}
      saveMessageId="save.collection.create.button"
      theme={theme}
      title={intl.formatMessage({ id: 'save.collection.create.title' })}
      values={{ name }}
    >
      <Form autoComplete="off" onSubmit={submitForm} error={firstError(saveErrors) !== null}>
        <FormValidationError
          error={codeError}
          htmlFor="collection_code"
          labelId="save.collection.code"
        >
          <Input
            id="collection_code"
            size="large"
            disabled={saveStatus === states.PENDING}
            type="custom"
            placeholder={intl.formatMessage({ id: 'save.collection.code.placeholder' })}
            value={code || ''}
            maxLength={globalFieldLengths.code}
            onChange={codeChange}
            className={validationClasses(codeError)}
          />
        </FormValidationError>
      </Form>
    </Dialog>
  );
};

const mapStateToProps = ({
  collections: {
    activeCollection: {
      code, name, description, saveErrors, saveStatus,
    },
  },
}) => ({
  code,
  name,
  description,
  saveErrors,
  saveStatus,
});

const mapDispatchToProps = {
  hide: hideSaveCollection,
  updateProperties: updateActiveCollectionProperties,
  postData: createCollection,
};

export default connect(mapStateToProps, mapDispatchToProps)(SaveCollectionDialog);
